import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";

import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import useMediaQuery from '@mui/material/useMediaQuery';

import { useTheme } from '@mui/material/styles';

import Api from '@mui/icons-material/Api';
import BugReport from '@mui/icons-material/BugReport';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import ContentPasteGo from '@mui/icons-material/ContentPasteGo';
import Home from '@mui/icons-material/Home';
import Info from '@mui/icons-material/Info';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import Logout from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import QuestionMark from '@mui/icons-material/QuestionMark';
import SyncLock from '@mui/icons-material/SyncLock';
import Tag from '@mui/icons-material/Tag';

import { Guide } from "../Guide/Guide";

export const ActionBar = ({ auth, title, environment, versions, onLogOut }) => {
    const [ menuAnchor, setMenuAnchor ] = useState(null);
    const [ showAbout, setShowAbout ] = useState(false);
    const [ showGuide, setShowGuide ] = useState(false);

    const closeButtonRef = useRef(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleShowGuide = () => {
        setShowGuide(true);
        handleMenuClose();
    };

    const handleChangePassword = () => {
        navigate('/help');
        handleMenuClose();
    };

    const handleBugReport = () => {
        navigate('/issue');
        handleMenuClose();
    }

    const handleShowAbout = () => {
        setShowAbout(true);
        handleMenuClose();
    };

    const handleAboutClose = () => {
        closeButtonRef.current?.blur();

        setShowAbout(false);
    };

    const handleGuideClose = () => {
        setShowGuide(false);
    };

    const handleLogOut = () => {
        onLogOut();
        handleMenuClose();
    }

    const menuItems = [
        { icon: <Home />, text: 'Dashboard', action: () => { navigate('/dashboard'); handleMenuClose(); }, public: auth?.currentUser, divider: true },
        { icon: <ContentPasteGo />, text: 'Go to Patrol Manager', action: () => { window.location.href = 'https://manage.patrol360.dev'; handleMenuClose(); }, public: auth?.currentUser },
        { icon: <CalendarMonth />, text: 'Go to Patrol Scheduler', action: () => { window.location.href = 'https://schedule.patrol360.dev'; handleMenuClose(); }, public: auth?.currentUser, divider: true },
        { icon: <QuestionMark />, text: 'User Guide', action: handleShowGuide, public: auth?.currentUser },
        { icon: <SyncLock />, text: 'Change Password', action: handleChangePassword, public: true, divider: !auth?.currentUser },
        { icon: <BugReport />, text: 'Report Bug', action: handleBugReport, public: auth?.currentUser, divider: auth?.currentUser },
        { icon: <Info />, text: 'About...', action: handleShowAbout, public: true, divider: auth?.currentUser },
        { icon: <Logout />, text: 'Log Out', action: handleLogOut, public: auth?.currentUser }
    ];

    return (
        <AppBar
            position="fixed"
            sx={ {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                mb: '1rem'
            } }
        >
            <Button
                onClick={ handleMenuOpen }
                color='primary'
                startIcon={ <MenuIcon /> }
                size='large'
                sx={ { width: isMobile ? '50px' : '120px' } }
            >
                { isMobile ? null : "Menu" }
            </Button>

            <Box
                sx={ {
                    border: '2px solid rgba(135, 206, 235, 0.3)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                } }
            >
                <Typography
                    variant={ isMobile ? "h6" : "h4" }
                    sx={ {
                        fontWeight: 'bold',
                        background: 'linear-gradient(45deg, #87CEEB 15%, #B0E0E6 75%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.05)',
                        letterSpacing: '0.5px',
                        margin: 0
                    } }
                >
                    { title || "Patrol360 Core" }
                </Typography>
            </Box>

            <Button disabled size='large'
                sx={ { visibility: environment !== 'PROD' ? 'visible' : 'hidden', width: isMobile ? '50px' : '120px' } }
            >
                { environment !== "PROD" && (
                    <Chip
                        label={ environment.substring(0, 4) }  // Show first 4 letters (e.g., "DEV", "STAG", "PROD")
                        color={ environment === "DEV" ? "error" : "warning" }  // Red for dev, yellow for staging
                        size={ isMobile ? "small" : "" }
                    />
                ) }
            </Button>

            <Menu anchorEl={ menuAnchor } open={ Boolean(menuAnchor) } onClose={ handleMenuClose }>
                { menuItems.map((item, index) => (
                    (item.public || auth?.currentUser) && (
                        <div key={ index }>
                            <MenuItem onClick={ item.action }>
                                <ListItemIcon>{ item.icon }</ListItemIcon>
                                <ListItemText>{ item.text }</ListItemText>
                            </MenuItem>

                            { item.divider && <Divider /> }
                        </div>
                    )
                )) }
            </Menu>

            <Dialog open={ showAbout } onClose={ handleAboutClose } disableEnforceFocus={ true } disableRestoreFocus={ true } sx={ { p: 2 } }>
                <DialogContent>
                    <Box sx={ { display: 'flex', flexDirection: 'column', gap: 1 } }>
                        <Chip icon={ <InsertDriveFile /> } label={ `${ versions?.site || 'N/A' }` } color="primary" variant="outlined" />
                        <Chip icon={ <Api /> } label={ `${ versions?.api || 'N/A' }` } color="secondary" variant="outlined" />
                        <Chip icon={ <Tag /> } label={ `${ versions?.commit || 'N/A' }` } color="success" variant="outlined" />
                    </Box>
                </DialogContent>

                <Divider variant='middle' />

                <DialogActions sx={ { justifyContent: 'center' } }>
                    <Button ref={ closeButtonRef } onClick={ handleAboutClose } color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            { showGuide && (
                isMobile ? (
                    <Guide topic="core-guide" onClose={ handleGuideClose } />
                ) : (
                    <Drawer
                        anchor="right"
                        open={ showGuide }
                        onClose={ handleGuideClose }
                        sx={ {
                            '& .MuiDrawer-paper': {
                                width: '50%',
                                maxWidth: '600px'
                            }
                        } }
                    >
                        <Guide topic="core-guide" onClose={ handleGuideClose } />
                    </Drawer>
                )
            ) }
        </AppBar>
    );
};